
import React from "react";
import PageLayout from "@/components/Layout/PageLayout";
import { HeroBanner } from "@/components/ui/hero-banner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Building, 
  Users, 
  UserCircle, 
  Bed, 
  Stethoscope, 
  Clipboard 
} from "lucide-react";

const HospitalPage: React.FC = () => {
  return (
    <PageLayout>
      <HeroBanner
        title="Hospital Dashboard"
        description="Manage hospital resources, staff, and patient care efficiently"
        backgroundColor="bg-gradient-to-r from-blue-700 to-blue-900"
      />

      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="departments">Departments</TabsTrigger>
            <TabsTrigger value="staff">Staff</TabsTrigger>
            <TabsTrigger value="patients">Patients</TabsTrigger>
            <TabsTrigger value="resources">Resources</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Bed Capacity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <Bed className="h-4 w-4 text-blue-600 mr-2" />
                    <div className="text-2xl font-bold">234/250</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">93.6% occupancy rate</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Medical Staff</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <Stethoscope className="h-4 w-4 text-blue-600 mr-2" />
                    <div className="text-2xl font-bold">128</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">42 doctors, 86 nurses</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Departments</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <Building className="h-4 w-4 text-blue-600 mr-2" />
                    <div className="text-2xl font-bold">12</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">3 specialized units</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Current Patients</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <Users className="h-4 w-4 text-blue-600 mr-2" />
                    <div className="text-2xl font-bold">187</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">24 new admissions today</p>
                </CardContent>
              </Card>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Hospital Performance</CardTitle>
                  <CardDescription>Key metrics for the current month</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-8">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium">Average Length of Stay</div>
                        <div className="text-sm text-blue-600 font-medium">4.2 days</div>
                      </div>
                      <div className="h-2 bg-blue-100 rounded-full">
                        <div className="h-2 bg-blue-600 rounded-full" style={{ width: '70%' }}></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium">Patient Satisfaction</div>
                        <div className="text-sm text-blue-600 font-medium">92%</div>
                      </div>
                      <div className="h-2 bg-blue-100 rounded-full">
                        <div className="h-2 bg-blue-600 rounded-full" style={{ width: '92%' }}></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium">Emergency Response Time</div>
                        <div className="text-sm text-blue-600 font-medium">3.4 min</div>
                      </div>
                      <div className="h-2 bg-blue-100 rounded-full">
                        <div className="h-2 bg-blue-600 rounded-full" style={{ width: '85%' }}></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium">Staff Utilization</div>
                        <div className="text-sm text-blue-600 font-medium">89%</div>
                      </div>
                      <div className="h-2 bg-blue-100 rounded-full">
                        <div className="h-2 bg-blue-600 rounded-full" style={{ width: '89%' }}></div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activities</CardTitle>
                  <CardDescription>Latest updates</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      "New patient admitted to Cardiology",
                      "Emergency room capacity at 85%",
                      "Staff scheduling updated for next week",
                      "Maintenance scheduled for MRI unit 2",
                      "Budget report submitted for review"
                    ].map((activity, i) => (
                      <div key={i} className="flex items-start">
                        <div className="mr-2 mt-0.5">
                          <Clipboard className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm">{activity}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(Date.now() - i * 2 * 60 * 60 * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="departments">
            <Card>
              <CardHeader>
                <CardTitle>Hospital Departments</CardTitle>
                <CardDescription>Overview of all departments and specialties</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    "Emergency Medicine",
                    "Cardiology",
                    "Neurology",
                    "Pediatrics",
                    "Oncology",
                    "Orthopedics",
                    "Radiology",
                    "Surgery",
                    "Obstetrics & Gynecology"
                  ].map((department, i) => (
                    <Card key={i} className="border shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">{department}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Staff:</span>
                            <span className="font-medium">{10 + i} physicians, {15 + i * 2} nurses</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Beds:</span>
                            <span className="font-medium">{20 + i * 2}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Current Patients:</span>
                            <span className="font-medium">{15 + i}</span>
                          </div>
                          <div className="mt-4">
                            <button className="text-blue-600 text-sm font-medium">View Details</button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="staff">
            <Card>
              <CardHeader>
                <CardTitle>Medical Staff Directory</CardTitle>
                <CardDescription>Manage hospital personnel</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Role</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Department</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Contact</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: 10 }).map((_, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">Dr. Name {i + 1}</td>
                          <td className="px-4 py-3 text-sm">
                            {i % 3 === 0 ? "Physician" : i % 3 === 1 ? "Nurse" : "Specialist"}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {["Cardiology", "Neurology", "Emergency", "Pediatrics", "Oncology"][i % 5]}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${i % 4 === 0 ? "bg-green-100 text-green-800" : i % 4 === 1 ? "bg-blue-100 text-blue-800" : i % 4 === 2 ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-800"}`}>
                              {i % 4 === 0 ? "On Duty" : i % 4 === 1 ? "In Surgery" : i % 4 === 2 ? "On Call" : "Off Duty"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">ext. {1000 + i}</td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex space-x-2">
                              <button className="text-blue-600 hover:text-blue-800 font-medium">View</button>
                              <button className="text-green-600 hover:text-green-800 font-medium">Schedule</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="patients">
            <Card>
              <CardHeader>
                <CardTitle>Current Patients</CardTitle>
                <CardDescription>Overview of patients currently admitted</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="px-4 py-3 text-left text-sm font-medium">Patient ID</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Department</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Admittance Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Attending Physician</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: 10 }).map((_, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">P-{10000 + i}</td>
                          <td className="px-4 py-3 text-sm">Patient Name {i + 1}</td>
                          <td className="px-4 py-3 text-sm">
                            {["Cardiology", "Neurology", "Emergency", "Pediatrics", "Oncology"][i % 5]}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {new Date(Date.now() - i * 2 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm">Dr. Smith {i % 5 + 1}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${i % 3 === 0 ? "bg-green-100 text-green-800" : i % 3 === 1 ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"}`}>
                              {i % 3 === 0 ? "Stable" : i % 3 === 1 ? "Under Observation" : "Critical"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex space-x-2">
                              <button className="text-blue-600 hover:text-blue-800 font-medium">Records</button>
                              <button className="text-green-600 hover:text-green-800 font-medium">Update</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="resources">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Equipment Inventory</CardTitle>
                  <CardDescription>Track hospital equipment status</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { name: "MRI Machines", total: 3, available: 2 },
                      { name: "CT Scanners", total: 4, available: 3 },
                      { name: "X-Ray Machines", total: 8, available: 5 },
                      { name: "Ventilators", total: 20, available: 12 },
                      { name: "Defibrillators", total: 15, available: 15 },
                      { name: "Ultrasound Machines", total: 6, available: 4 },
                    ].map((equipment, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{equipment.name}</p>
                          <p className="text-sm text-gray-500">
                            {equipment.available} of {equipment.total} available
                          </p>
                        </div>
                        <div className="w-20 h-2 bg-gray-200 rounded-full">
                          <div 
                            className="h-2 bg-blue-600 rounded-full" 
                            style={{ width: `${(equipment.available / equipment.total) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Room Availability</CardTitle>
                  <CardDescription>Current status of hospital rooms</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 border rounded-lg bg-blue-50">
                      <p className="text-sm font-medium">ICU Beds</p>
                      <p className="text-2xl font-bold mt-1">12/15</p>
                      <p className="text-xs text-gray-500 mt-1">80% occupied</p>
                    </div>
                    <div className="p-3 border rounded-lg bg-green-50">
                      <p className="text-sm font-medium">Operating Rooms</p>
                      <p className="text-2xl font-bold mt-1">6/8</p>
                      <p className="text-xs text-gray-500 mt-1">75% in use</p>
                    </div>
                    <div className="p-3 border rounded-lg bg-purple-50">
                      <p className="text-sm font-medium">General Wards</p>
                      <p className="text-2xl font-bold mt-1">156/200</p>
                      <p className="text-xs text-gray-500 mt-1">78% occupied</p>
                    </div>
                    <div className="p-3 border rounded-lg bg-yellow-50">
                      <p className="text-sm font-medium">Emergency Beds</p>
                      <p className="text-2xl font-bold mt-1">18/25</p>
                      <p className="text-xs text-gray-500 mt-1">72% occupied</p>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <h4 className="text-sm font-medium mb-2">Special Units</h4>
                    <div className="space-y-2">
                      {[
                        { name: "Burn Unit", occupied: 3, total: 5 },
                        { name: "Maternity", occupied: 12, total: 15 },
                        { name: "Pediatric ICU", occupied: 4, total: 6 },
                        { name: "Isolation Rooms", occupied: 8, total: 10 },
                      ].map((unit, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <p className="text-sm">{unit.name}</p>
                          <p className="text-sm font-medium">{unit.occupied}/{unit.total}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
};

export default HospitalPage;
